'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { toast } from '@/shared/ui/use-toast';


const schema = z.object({
    email   : z.string().email(),
    password: z.string().min(6),
});

type FormValues = z.infer<typeof schema>;

export function LoginForm () {
    const router                  = useRouter();
    const sp                      = useSearchParams();
    const next                    = sp.get('next') || '/app/links/';
    const [ loading, setLoading ] = useState(false);

    const form = useForm<FormValues>({
        resolver     : zodResolver(schema),
        defaultValues: { email: '', password: '' },
    });

    async function onSubmit ({ email, password }: FormValues) {
        setLoading(true);

        try {
            const res = await fetch('/api/v1/auth/login', {
                method : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body   : JSON.stringify({ email, password }),
            });

            if (res.ok) {
                toast({ title: 'Вход выполнен', description: email });
                console.log('redirecting to:', next);
                router.replace(next);
            } else {
                const data = await res.json();
                toast({ title: 'Ошибка входа', description: data.error?.message || 'Неизвестная ошибка', variant: 'destructive' });
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                toast({ title: 'Ошибка входа', description: error?.message || 'Сетевая ошибка', variant: 'destructive' });
            } else {
                toast({ title: 'Ошибка входа', description: 'Сетевая ошибка', variant: 'destructive' });
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <Card className="w-full max-w-md border-muted/70">
            <CardHeader>
                <CardTitle className="text-lg">Log in</CardTitle>
            </CardHeader>
            <CardContent>
                <form onSubmit={ form.handleSubmit(onSubmit) } className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            className="h-11"
                            inputMode="email"
                            autoComplete="email"
                            { ...form.register('email') }
                        />
                        { form.formState.errors.email && (
                            <p className="text-xs text-destructive">{ form.formState.errors.email.message }</p>
                        ) }
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="password">Password</Label>
                        <Input
                            id="password"
                            className="h-11"
                            type="password"
                            autoComplete="current-password"
                            { ...form.register('password') }
                        />
                        { form.formState.errors.password && (
                            <p className="text-xs text-destructive">{ form.formState.errors.password.message }</p>
                        ) }
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <Button type="submit" className="h-11 w-full sm:w-auto" disabled={ loading }>
                            { loading ? 'Входим…' : 'Log in' }
                        </Button>
                        <Button asChild variant="ghost" className="h-11 w-full sm:w-auto">
                            <Link href="/forgot">Забыли пароль?</Link>
                        </Button>
                    </div>

                    <p className="text-sm text-muted-foreground">
                        Нет аккаунта?{ ' ' }
                        <Link className="text-foreground underline underline-offset-4" href="/register">
                            Зарегистрироваться
                        </Link>
                    </p>
                </form>
            </CardContent>
        </Card>
    );
}
